/**
 * Component to surface per-member reply probability inside a recipient
 * list. Designed to be embedded as one cell of a larger row, OR rendered
 * as a sortable column wrapper around an existing list.
 *
 * Two modes:
 *   1. Single-cell: <ReplyProbabilityCell memberId={n} />
 *      — Used inside an existing rendered row. Each call triggers an
 *        individual fetch (deduped by React Query queryKey).
 *
 *   2. Batched: useReplyProbabilityMap(memberIds) → Map<memberId, prob>
 *      — Preferred for long lists; one HTTP call scores everyone.
 *
 * Plus: ReplyProbabilityHeader has a sort toggle the parent list can use.
 */

import React from 'react';
import { ArrowDownAZ } from 'lucide-react';
import { useReplyProbability, useReplyProbabilityBatch } from '@/hooks/ml';
import { PredictionConfidenceBar } from '@/components/ml';

// ---------------------------------------------------------------------------
// Single-cell variant (use sparingly — N members = N requests, even if deduped)
// ---------------------------------------------------------------------------

export const ReplyProbabilityCell: React.FC<{ memberId: number }> = ({ memberId }) => {
  const { data, isLoading, isError, error } = useReplyProbability(memberId);
  if (isError && error instanceof Error && error.message === 'model_not_trained') {
    return <span className="text-xs text-gray-400">—</span>;
  }
  if (isLoading) return <span className="text-xs text-gray-400">…</span>;
  if (!data) return <span className="text-xs text-gray-400">—</span>;
  return <PredictionConfidenceBar probability={data.probability} variant="compact" />;
};

// ---------------------------------------------------------------------------
// Batched variant (use for lists > ~5 members)
// ---------------------------------------------------------------------------

export interface ReplyProbabilityMap {
  byId: Record<number, number | null>;
  isLoading: boolean;
  isError: boolean;
  notTrained: boolean;
}

export function useReplyProbabilityMap(memberIds: number[]): ReplyProbabilityMap {
  const { data, isLoading, isError, error } = useReplyProbabilityBatch(memberIds);
  const byId: Record<number, number | null> = {};
  for (const id of memberIds) byId[id] = null;
  if (data) {
    for (const s of data.scores) byId[s.member_id] = s.probability;
  }
  return {
    byId,
    isLoading,
    isError,
    notTrained: error instanceof Error && error.message === 'model_not_trained',
  };
}

// ---------------------------------------------------------------------------
// Sort header — drop into an existing list header
// ---------------------------------------------------------------------------

export const ReplyProbabilityHeader: React.FC<{
  active?: boolean;
  direction?: 'asc' | 'desc';
  onToggle?: () => void;
}> = ({ active = false, direction = 'desc', onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    className={`inline-flex items-center gap-1 text-xs uppercase tracking-wide ${
      active ? 'text-indigo-700 font-semibold' : 'text-gray-500'
    } hover:text-indigo-700`}
  >
    Reply prob.
    <ArrowDownAZ
      className={`h-3 w-3 ${active && direction === 'asc' ? 'rotate-180' : ''} transition-transform`}
    />
  </button>
);
